import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../components/ui/table";
import { 
  Plus, Calendar as CalendarIcon, Palmtree, CheckCircle, XCircle, Clock,
  Sun, Umbrella, Baby, Heart
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const leaveTypes = [
  { id: "vacation", label: "Vacaciones", icon: Palmtree, color: "text-emerald-600" },
  { id: "sick", label: "Enfermedad", icon: Heart, color: "text-red-600" },
  { id: "personal", label: "Personal", icon: Sun, color: "text-amber-600" },
  { id: "maternity", label: "Maternidad", icon: Baby, color: "text-pink-600" },
  { id: "paternity", label: "Paternidad", icon: Baby, color: "text-blue-600" },
  { id: "bereavement", label: "Duelo", icon: Umbrella, color: "text-slate-600" },
  { id: "remote_work", label: "Trabajo remoto", icon: CalendarIcon, color: "text-violet-600" }
];

const Vacations = () => {
  const { user } = useAuth();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [vacationBalance, setVacationBalance] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [corporateBalance, setCorporateBalance] = useState(null);
  const [workCenterFilter, setWorkCenterFilter] = useState("all");
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  const [formData, setFormData] = useState({
    employee_id: "",
    leave_type: "",
    start_date: "",
    end_date: "",
    reason: ""
  });
  const [holidayForm, setHolidayForm] = useState({ name: "", date: "", type: "company", holiday_scope: "company", work_center_id: "", is_official: false, non_working_day: true });

  const isEmployee = user?.role === "empleado";
  const canApprove = ["super_admin", "admin_empresa", "gerente_rh", "supervisor"].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, [workCenterFilter]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [leavesRes, holidaysRes] = await Promise.all([
        axios.get(`${API}/leave-requests`, { headers, withCredentials: true }),
        axios.get(`${API}/holidays`, { headers, withCredentials: true })
      ]);
      
      setLeaveRequests(leavesRes.data);
      setHolidays(holidaysRes.data);
      
      // Get employees for admin users
      if (!isEmployee) {
        const empRes = await axios.get(`${API}/employees`, { headers, withCredentials: true });
        setEmployees(empRes.data);
        
        // Get balance for first employee or logged user
        if (empRes.data.length > 0) {
          const balanceRes = await axios.get(`${API}/vacation-balance/${empRes.data[0].employee_id}`, {
            headers, withCredentials: true
          });
          setVacationBalance(balanceRes.data);
        }
        const params = workCenterFilter !== "all" ? `?work_center_id=${workCenterFilter}` : "";
        const corporateRes = await axios.get(`${API}/vacations/corporate-balance${params}`, { headers, withCredentials: true }).catch(() => ({ data: null }));
        setCorporateBalance(corporateRes.data);
      }
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.leave_type || !formData.start_date || !formData.end_date) {
      toast.error("Completa los campos requeridos");
      return;
    }
    
    // For employees, auto-assign their employee_id
    let employee_id = formData.employee_id;
    if (isEmployee && employees.length > 0) {
      const myEmployee = employees.find(e => e.email === user?.email);
      if (myEmployee) employee_id = myEmployee.employee_id;
    }
    
    if (!employee_id) {
      toast.error("Selecciona un empleado");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/leave-requests`, {
        ...formData,
        employee_id
      }, { headers, withCredentials: true });
      
      toast.success("Solicitud enviada");
      setDialogOpen(false);
      setFormData({ employee_id: "", leave_type: "", start_date: "", end_date: "", reason: "" });
      fetchData();
    } catch (error) {
      toast.error("Error al enviar solicitud");
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/leave-requests/${leaveId}/approve`, {}, { headers, withCredentials: true });
      toast.success("Solicitud aprobada");
      fetchData();
    } catch (error) {
      toast.error("Error al aprobar solicitud");
    }
  };

  const handleReject = async (leaveId) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/leave-requests/${leaveId}/reject`, {}, { headers, withCredentials: true });
      toast.success("Solicitud rechazada");
      fetchData();
    } catch (error) {
      toast.error("Error al rechazar solicitud");
    }
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.employee_id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : employeeId;
  };

  const workCenters = [...new Set(employees.map(employee => employee.work_center_id ? JSON.stringify({ id: employee.work_center_id, name: employee.work_center_name }) : null).filter(Boolean))].map(item => JSON.parse(item));

  const getLeaveTypeInfo = (typeId) => leaveTypes.find(t => t.id === typeId) || { label: typeId, icon: CalendarIcon, color: "text-slate-600" };

  const pendingRequests = leaveRequests.filter(r => r.status === "pending");
  const approvedRequests = leaveRequests.filter(r => r.status === "approved");

  // Calendar highlight dates
  const holidayDates = holidays.map(h => new Date(h.date));
  const approvedDates = approvedRequests.flatMap(r => {
    const dates = [];
    const start = new Date(r.start_date);
    const end = new Date(r.end_date);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d));
    }
    return dates;
  });

  const handleCreateHoliday = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/holidays`, { ...holidayForm, work_center_id: holidayForm.work_center_id || undefined }, { headers, withCredentials: true });
      toast.success("Feriado configurado");
      setHolidayDialogOpen(false);
      setHolidayForm({ name: "", date: "", type: "company", holiday_scope: "company", work_center_id: "", is_official: false, non_working_day: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo guardar el feriado");
    }
  };

  return (
    <Layout 
      title="Vacaciones y Permisos" 
      subtitle="Gestiona tus días libres y solicitudes"
      actions={
        <div className="flex gap-2 flex-wrap">
        {canApprove && <Button variant="outline" onClick={() => setHolidayDialogOpen(true)} data-testid="configure-holiday-btn">Configurar feriado</Button>}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="request-vacation-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Solicitud
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Solicitar Permiso</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isEmployee && (
                <div className="space-y-2">
                  <Label>Empleado *</Label>
                  <Select value={formData.employee_id} onValueChange={(v) => setFormData(p => ({ ...p, employee_id: v }))}>
                    <SelectTrigger data-testid="vacation-employee-select">
                      <SelectValue placeholder="Selecciona empleado" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(e => (
                        <SelectItem key={e.employee_id} value={e.employee_id}>
                          {e.first_name} {e.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Tipo de Permiso *</Label>
                <Select value={formData.leave_type} onValueChange={(v) => setFormData(p => ({ ...p, leave_type: v }))}>
                  <SelectTrigger data-testid="vacation-type-select">
                    <SelectValue placeholder="Selecciona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {leaveTypes.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        <div className="flex items-center gap-2">
                          <t.icon className={`w-4 h-4 ${t.color}`} />
                          {t.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Inicio *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(p => ({ ...p, start_date: e.target.value }))}
                    data-testid="vacation-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Fin *</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(p => ({ ...p, end_date: e.target.value }))}
                    data-testid="vacation-end-date"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Motivo</Label>
                <Textarea
                  value={formData.reason}
                  onChange={(e) => setFormData(p => ({ ...p, reason: e.target.value }))}
                  placeholder="Describe el motivo..."
                  rows={2}
                  data-testid="vacation-reason"
                />
              </div>
              
              <DialogFooter>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-vacation-btn">
                  Enviar Solicitud
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      }
    >
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendar & Balance */}
        <div className="lg:col-span-1 space-y-6">
          {corporateBalance && canApprove && (
            <Card className="border-slate-200 bg-gradient-to-br from-sky-50 to-white" data-testid="corporate-vacation-balance-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between gap-2">
                  <span>Balance corporativo</span>
                  <Select value={workCenterFilter} onValueChange={setWorkCenterFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="vacation-work-center-filter"><SelectValue placeholder="Centro" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toda la empresa</SelectItem>
                      {workCenters.map(center => <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-white border border-slate-200 text-center"><p className="text-xs text-slate-400 uppercase">Disponibles</p><p className="text-2xl font-bold text-slate-900">{corporateBalance.totals.available_days}</p></div>
                  <div className="p-3 rounded-lg bg-white border border-slate-200 text-center"><p className="text-xs text-slate-400 uppercase">Usados</p><p className="text-2xl font-bold text-slate-900">{corporateBalance.totals.used_days}</p></div>
                  <div className="p-3 rounded-lg bg-white border border-slate-200 text-center"><p className="text-xs text-slate-400 uppercase">Pendientes</p><p className="text-2xl font-bold text-slate-900">{corporateBalance.totals.pending_days}</p></div>
                  <div className="p-3 rounded-lg bg-white border border-slate-200 text-center"><p className="text-xs text-slate-400 uppercase">Remoto</p><p className="text-2xl font-bold text-slate-900">{corporateBalance.totals.remote_work_requests}</p></div>
                </div>
                <p className="text-xs text-slate-500">Empleados visibles: {corporateBalance.totals.employees} · Feriados configurados: {corporateBalance.totals.configured_holidays}</p>
              </CardContent>
            </Card>
          )}
          {/* Balance Card */}
          {vacationBalance && (
            <Card className="border-slate-200 bg-gradient-to-br from-teal-50 to-white">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palmtree className="w-5 h-5 text-teal-600" />
                  Saldo de Vacaciones {vacationBalance.year}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 rounded-lg bg-white border border-teal-100">
                    <p className="text-3xl font-bold text-teal-600">{vacationBalance.available_days}</p>
                    <p className="text-sm text-slate-500">Disponibles</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-white border border-slate-100">
                    <p className="text-3xl font-bold text-slate-600">{vacationBalance.used_days}</p>
                    <p className="text-sm text-slate-500">Usados</p>
                  </div>
                </div>
                <div className="mt-4 flex justify-between text-sm">
                  <span className="text-slate-500">Total anual:</span>
                  <span className="font-medium">{vacationBalance.total_days} días</span>
                </div>
                {vacationBalance.pending_days > 0 && (
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-amber-600">Pendientes de aprobación:</span>
                    <span className="font-medium text-amber-600">{vacationBalance.pending_days} días</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Calendar */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Calendario</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md border"
                modifiers={{
                  holiday: holidayDates,
                  approved: approvedDates
                }}
                modifiersStyles={{
                  holiday: { backgroundColor: '#fef3c7', color: '#d97706' },
                  approved: { backgroundColor: '#d1fae5', color: '#059669' }
                }}
              />
            </CardContent>
            <div className="px-6 pb-4 flex gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-amber-100"></div>
                <span className="text-slate-500">Día festivo</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-emerald-100"></div>
                <span className="text-slate-500">Permiso aprobado</span>
              </div>
            </div>
          </Card>

          {/* Holidays List */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Días Festivos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {holidays.slice(0, 5).map((holiday) => (
                  <div key={holiday.holiday_id} className="flex items-center justify-between p-2 rounded bg-amber-50">
                    <span className="text-sm font-medium">{holiday.name}</span>
                    <span className="text-xs text-slate-500">
                      {new Date(holiday.date).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Requests */}
        <div className="lg:col-span-2 space-y-6">
          {/* Pending Requests */}
          {canApprove && pendingRequests.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-600" />
                  Solicitudes Pendientes ({pendingRequests.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingRequests.map((request) => {
                    const typeInfo = getLeaveTypeInfo(request.leave_type);
                    return (
                      <div key={request.leave_id} className="flex items-center justify-between p-4 rounded-lg bg-white border border-amber-200">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center`}>
                            <typeInfo.icon className={`w-5 h-5 ${typeInfo.color}`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{getEmployeeName(request.employee_id)}</p>
                            <p className="text-sm text-slate-500">
                              {typeInfo.label} • {request.days_requested} día(s)
                            </p>
                            <p className="text-xs text-slate-400">
                              {new Date(request.start_date).toLocaleDateString('es-MX')} - {new Date(request.end_date).toLocaleDateString('es-MX')}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(request.leave_id)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`approve-${request.leave_id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleReject(request.leave_id)}
                            data-testid={`reject-${request.leave_id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* All Requests */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Historial de Solicitudes</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : leaveRequests.length === 0 ? (
                <div className="text-center text-slate-500 py-8">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No hay solicitudes</p>
                </div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empleado</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Fechas</TableHead>
                      <TableHead>Días</TableHead>
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaveRequests.map((request) => {
                      const typeInfo = getLeaveTypeInfo(request.leave_type);
                      return (
                        <TableRow key={request.leave_id}>
                          <TableCell className="font-medium">{getEmployeeName(request.employee_id)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <typeInfo.icon className={`w-4 h-4 ${typeInfo.color}`} />
                              {typeInfo.label}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {new Date(request.start_date).toLocaleDateString('es-MX')} - {new Date(request.end_date).toLocaleDateString('es-MX')}
                          </TableCell>
                          <TableCell>{request.days_requested}</TableCell>
                          <TableCell>
                            <Badge className={
                              request.status === "approved" ? "badge-success" :
                              request.status === "rejected" ? "badge-error" : "badge-warning"
                            }>
                              {request.status === "approved" ? "Aprobado" :
                               request.status === "rejected" ? "Rechazado" : "Pendiente"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={holidayDialogOpen} onOpenChange={setHolidayDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Configurar feriado / día no laborable</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nombre</Label><Input value={holidayForm.name} onChange={(event) => setHolidayForm(previous => ({ ...previous, name: event.target.value }))} data-testid="holiday-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Fecha</Label><Input type="date" value={holidayForm.date} onChange={(event) => setHolidayForm(previous => ({ ...previous, date: event.target.value }))} data-testid="holiday-date-input" /></div>
              <div className="space-y-2"><Label>Ámbito</Label><Select value={holidayForm.holiday_scope} onValueChange={(value) => setHolidayForm(previous => ({ ...previous, holiday_scope: value }))}><SelectTrigger data-testid="holiday-scope-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="company">Empresa</SelectItem><SelectItem value="work_center">Unidad de operación</SelectItem></SelectContent></Select></div>
            </div>
            {holidayForm.holiday_scope === "work_center" && <div className="space-y-2"><Label>Centro</Label><Select value={holidayForm.work_center_id || "none"} onValueChange={(value) => setHolidayForm(previous => ({ ...previous, work_center_id: value === "none" ? "" : value }))}><SelectTrigger data-testid="holiday-work-center-select"><SelectValue placeholder="Selecciona un centro" /></SelectTrigger><SelectContent><SelectItem value="none">Sin centro</SelectItem>{workCenters.map(center => <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>)}</SelectContent></Select></div>}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Tipo</Label><Select value={holidayForm.type} onValueChange={(value) => setHolidayForm(previous => ({ ...previous, type: value }))}><SelectTrigger data-testid="holiday-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="company">Corporativo</SelectItem><SelectItem value="national">Nacional</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Día no laborable</Label><Select value={String(holidayForm.non_working_day)} onValueChange={(value) => setHolidayForm(previous => ({ ...previous, non_working_day: value === "true" }))}><SelectTrigger data-testid="holiday-non-working-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="true">Sí</SelectItem><SelectItem value="false">No</SelectItem></SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter><Button onClick={handleCreateHoliday} className="bg-sky-600 hover:bg-sky-700" data-testid="save-holiday-btn">Guardar feriado</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Vacations;
